import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Printer, ShieldCheck, TrendingUp, Wallet, Boxes, ArrowRight, CheckCircle2, Loader2, PhoneCall } from "lucide-react";
import TermsAcceptBlock from "@/components/TermsAcceptBlock";

const MSG91_WIDGET_ID = process.env.REACT_APP_MSG91_WIDGET_ID || "";
const MSG91_WIDGET_TOKEN = process.env.REACT_APP_MSG91_WIDGET_TOKEN || "";
const MSG91_SCRIPT_URL = "https://verify.msg91.com/otp-provider.js";

const MACHINE_CATEGORIES = [
  { key: "sla", label: "SLA (Formlabs Form 3+/4)" },
  { key: "dlp", label: "DLP (Asiga MAX/PRO, EnvisionTEC)" },
  { key: "msla", label: "MSLA (Phrozen Sonic, Elegoo Saturn)" },
  { key: "wax_jet", label: "Wax-Jet / MJP (3D Systems ProJet, Solidscape, Flashforge)" },
  { key: "fdm", label: "FDM (non-castable prototypes)" },
];

const BENEFITS = [
  { icon: TrendingUp, title: "Fill your idle print hours", body: "We route paid print jobs from jewellery buyers to shops with matching machinery — no hunting for clients." },
  { icon: Wallet, title: "Payments before you print", body: "Buyers pay through Jeweltix upfront. You start printing only after the money lands in escrow." },
  { icon: ShieldCheck, title: "Escrow-protected settlement", body: "48-hour hold on every job. If nothing's disputed, funds auto-release to your wallet. No chasing manufacturers." },
  { icon: Boxes, title: "STL only — designer IP stays safe", body: "You'll never see the editable 3DM file. Every handoff is logged. Peace of mind for both sides." },
];

export default function VendorApply() {
  const [form, setForm] = useState({
    shop_name: "",
    owner_name: "",
    email: "",
    mobile: "",
    city: "",
    machines: [],
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [otpToken, setOtpToken] = useState(null); // MSG91 access-token once mobile is verified
  const [verifying, setVerifying] = useState(false);
  const [acceptState, setAcceptState] = useState({ accepted: false, checkboxes: [] });
  const scriptReadyRef = useRef(false);

  // Load MSG91 widget script once, lazily. Guarded so React Strict Mode's
  // double-invoke doesn't inject two copies.
  useEffect(() => {
    if (!MSG91_WIDGET_TOKEN) return; // integration not configured yet — hide OTP UX
    if (typeof window !== "undefined" && typeof window.initSendOTP === "function") {
      scriptReadyRef.current = true;
      return;
    }
    if (document.querySelector(`script[src="${MSG91_SCRIPT_URL}"]`)) return;
    const s = document.createElement("script");
    s.src = MSG91_SCRIPT_URL;
    s.async = true;
    s.onload = () => { scriptReadyRef.current = typeof window.initSendOTP === "function"; };
    s.onerror = () => { console.warn("MSG91 script failed to load"); };
    document.body.appendChild(s);
  }, []);

  const toggleMachine = (k) => {
    setForm((f) => ({ ...f, machines: f.machines.includes(k) ? f.machines.filter((m) => m !== k) : [...f.machines, k] }));
  };

  /** Open MSG91 OTP modal. On success, the widget returns an access-token
   *  which we send to the backend for cryptographic verification against
   *  MSG91's servers — never trust the browser callback alone. */
  const verifyMobile = () => {
    const digits = form.mobile.replace(/\D/g, "");
    if (digits.length < 10) return toast.error("Please enter a valid mobile number with country code first.");
    if (!MSG91_WIDGET_TOKEN) return toast.error("OTP verification isn't configured yet. Skip this step and our team will call you.");
    if (typeof window.initSendOTP !== "function") return toast.error("OTP widget is still loading — try again in a second.");
    setVerifying(true);
    window.initSendOTP({
      widgetId: MSG91_WIDGET_ID,
      tokenAuth: MSG91_WIDGET_TOKEN,
      identifier: digits,
      exposeMethods: false,
      success: async (data) => {
        // MSG91 returns the access-token under different keys across accounts.
        const token = data?.message || data?.token || data?.accessToken || data?.["access-token"];
        if (!token) {
          setVerifying(false);
          return toast.error("OTP verified but no token returned. Please try again.");
        }
        try {
          await api.post("/otp/msg91/verify", { access_token: token, identifier: digits });
          setOtpToken(token);
          toast.success("Mobile verified ✓");
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Server rejected the OTP. Try again.");
        } finally {
          setVerifying(false);
        }
      },
      failure: (err) => {
        setVerifying(false);
        toast.error(err?.message || "OTP verification failed or was cancelled.");
      },
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.shop_name || !form.owner_name || !form.email || !form.mobile || !form.city) {
      return toast.error("Please fill in shop name, owner, email, mobile, and city.");
    }
    if (!acceptState.accepted) {
      return toast.error("Please read and tick all required consent boxes to submit your application.");
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        accept_terms: true,
        terms_version: "1.0",
        checkboxes: acceptState.checkboxes,
      };
      if (otpToken) payload.msg91_access_token = otpToken;
      const { data } = await api.post("/vendor/waitlist", payload);
      setDone(true);
      toast.success(data?.message || "You're on the waitlist!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div
          aria-hidden
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: "linear-gradient(rgba(148,163,184,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.08) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />
        <div
          aria-hidden
          className="absolute -top-24 -right-32 h-96 w-96 rounded-full blur-3xl opacity-40"
          style={{ background: "radial-gradient(closest-side, #3b82f6, transparent)" }}
        />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 pt-16 pb-20">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.25em] text-blue-300 border border-blue-400/30 rounded-full px-3 py-1 mb-6" data-testid="vendor-hero-badge">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" /> Now onboarding — free at launch
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
            Turn your 3D printers into a <span className="text-blue-400">steady order pipeline</span>.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl">
            Jeweltix is where jewellery manufacturers buy CAD files. Now they need a shop to <em className="not-italic text-white">print</em> those files. That's you.
            Get discoverable, get paid upfront, and let escrow protect every job.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#apply" data-testid="vendor-hero-apply" className="inline-flex items-center gap-2 rounded-md bg-blue-500 hover:bg-blue-400 text-white px-5 py-3 font-semibold text-sm transition-colors">
              Join the waitlist <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/how-it-works" className="inline-flex items-center gap-2 rounded-md border border-slate-700 hover:border-blue-400 hover:text-blue-300 px-5 py-3 font-semibold text-sm transition-colors">
              How it works
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-widest text-slate-400">
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Free listing at launch</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Escrow-protected</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Set your own rate/gram</span>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1200px] mx-auto px-4 sm:px-6 py-16">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-3">Why Jeweltix</p>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight max-w-2xl">
          Built for print shops. Not another lead-gen middleman.
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.title} className="border border-border rounded-lg bg-card p-5">
              <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4">
                <b.icon className="h-5 w-5" strokeWidth={1.6} />
              </div>
              <h3 className="font-semibold text-sm mb-1.5">{b.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-14">
          <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-3">Pricing</p>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight">Free while we grow together.</h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-2xl leading-relaxed">
            Onboarding is free at launch — we want a strong vendor network before we monetise listings.
            Later, listing packages start at <strong className="text-foreground">₹1,899 / month</strong> (with 3/6/9/12-month options at a discount).
            Existing waitlist vendors get priority pricing.
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { m: "1", p: "1,899" },
              { m: "3", p: "2,999" },
              { m: "6", p: "5,999" },
              { m: "9", p: "8,999" },
              { m: "12", p: "11,999" },
            ].map((tier) => (
              <div key={tier.m} className="border border-border rounded-lg bg-card p-4 text-center" data-testid={`vendor-pricing-${tier.m}`}>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{tier.m} Month{tier.m === "1" ? "" : "s"}</p>
                <p className="font-display font-extrabold text-xl mt-1">₹{tier.p}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[11px] text-muted-foreground font-mono">
            * 10% platform margin on every job you accept applies from day one, regardless of listing tier.
          </p>
        </div>
      </section>

      {/* Application form */}
      <section id="apply" className="max-w-[720px] mx-auto px-4 sm:px-6 py-16">
        <p className="text-[11px] font-mono uppercase tracking-[0.3em] text-primary mb-3">Application</p>
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl leading-tight">Tell us about your shop.</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Two minutes. We'll get back to you within 48 hours to onboard your account and verify your mobile number.
        </p>

        {done ? (
          <div className="mt-8 border border-emerald-500/40 bg-emerald-500/10 rounded-lg p-8 text-center" data-testid="vendor-apply-success">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
            <h3 className="mt-4 font-display font-bold text-xl">You're in.</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
              Application received. Our team will reach out on <strong className="text-foreground">{form.mobile}</strong> shortly to verify your shop and activate your dashboard.
            </p>
            <Link to="/" className="mt-6 inline-flex items-center gap-2 text-sm text-primary hover:underline">
              Back to homepage <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4 border border-border rounded-lg bg-card p-5 sm:p-6" data-testid="vendor-apply-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Shop / business name *" value={form.shop_name} onChange={(v) => setForm({ ...form, shop_name: v })} testid="vendor-shop-name" />
              <Field label="Owner name *" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} testid="vendor-owner-name" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="vendor-email" />
              <div>
                <label className="text-sm font-medium">Mobile (with country code) *</label>
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    value={form.mobile}
                    onChange={(e) => { setForm({ ...form, mobile: e.target.value }); if (otpToken) setOtpToken(null); }}
                    placeholder="+91 98xxxxxxxx"
                    data-testid="vendor-mobile"
                    className="flex-1 border border-border rounded-md p-2.5 text-sm bg-background"
                  />
                  {MSG91_WIDGET_TOKEN && (
                    otpToken ? (
                      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 px-2.5 text-xs font-semibold" data-testid="vendor-mobile-verified">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Verified
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={verifyMobile}
                        disabled={verifying || !form.mobile.trim()}
                        data-testid="vendor-verify-mobile"
                        className="inline-flex items-center gap-1.5 rounded-md border border-primary/40 text-primary px-3 text-xs font-semibold hover:bg-primary/10 disabled:opacity-50 transition-colors"
                      >
                        {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PhoneCall className="h-3.5 w-3.5" />}
                        {verifying ? "Verifying…" : "Verify OTP"}
                      </button>
                    )
                  )}
                </div>
              </div>
            </div>
            <Field label="City *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} placeholder="Mumbai / Delhi / Surat / …" testid="vendor-city" />

            <div>
              <label className="text-sm font-medium">Machinery owned (tick all that apply)</label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {MACHINE_CATEGORIES.map((m) => (
                  <label key={m.key} className="flex items-start gap-2 text-xs cursor-pointer border border-border rounded-md p-2.5 hover:border-primary transition-colors" data-testid={`vendor-machine-${m.key}`}>
                    <input
                      type="checkbox"
                      checked={form.machines.includes(m.key)}
                      onChange={() => toggleMachine(m.key)}
                      className="mt-0.5 accent-primary"
                    />
                    <span className="leading-snug">{m.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Anything you'd like us to know?</label>
              <textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                data-testid="vendor-message"
                placeholder="Years in business, sample work, existing clients, etc."
                className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
              />
            </div>

            <TermsAcceptBlock role="cam_vendor" onChange={setAcceptState} />

            <button
              type="submit"
              disabled={sending || !acceptState.accepted}
              data-testid="vendor-submit"
              className="w-full flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground py-3 font-semibold text-sm hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {sending ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting…</> : <>Join the waitlist <ArrowRight className="h-4 w-4" /></>}
            </button>
            <p className="text-[11px] text-muted-foreground text-center">
              By applying, you agree to receive a verification call on the mobile number above.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="text-sm font-medium">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
      />
    </div>
  );
}
